import React from 'react';
import { CSSObject, forBrands, Theme } from '@core-ui/react-stitch';
import { getButtonBackgroundColorForBananaRepublic, getButtonTextColorForBananaRepublic } from '../Style/BRRedesignStyles';

type SizeButtonProps = {
  name: string,
  style: string,
  applied: boolean,
  description?: string,
  variant: string,
  onClick: () => void
  isBRRedesignEnabled: boolean,
};

const SizeButton = ({
  name,
  style,
  applied,
  description,
  variant,
  onClick,
  isBRRedesignEnabled,
}: SizeButtonProps) => {

  const getSizeButtonStyleForGap = (t: Theme) => ({
    lineHeight: '0.9',
    backgroundColor: applied ? t.color.b1 : t.color.wh,
    color: applied ? t.color.wh : t.color.b2,
    borderRadius: 25,
  });

  const getBackgroundColor = (applied: any, t: Theme) => {
    if (applied) return t.color.b1;
    return t.color.wh;
  };

  const getColor = (applied: any, t: Theme) => {
    if (applied) return t.color.wh;
    return t.color.b1;
  };

  const getSizeButtonStyleForOldNavy = (t: Theme): CSSObject => ({
    lineHeight: '1.05',
    backgroundColor: getBackgroundColor(applied, t),
    color: getColor(applied, t),
    fontSize: '0.75rem',
    borderRadius: applied ? '24px' : 'inherit',
  });

  const getSizeButtonStyleForAthleta = (t: Theme): CSSObject => ({
    lineHeight: '0.9',
    // @ts-ignore
    backgroundColor: applied ? t.color.g6 : t.color.wh,
    color: t.color.b1,
    borderRadius: '20px',
  });

  const getSizeButtonStyleForBananaRepublic = (t: Theme): CSSObject => ({
    lineHeight: '0.9',
    backgroundColor: getButtonBackgroundColorForBananaRepublic(t, isBRRedesignEnabled, applied),
    color: getButtonTextColorForBananaRepublic(t, isBRRedesignEnabled, applied),
    padding: '0.375rem 0.5rem',
    ...(isBRRedesignEnabled && { fontSize: '0.875rem' }),
    ...(isBRRedesignEnabled && { fontWeight: 100 }),
  });

  const getHoverStyle = (t: Theme): CSSObject => ({
    // @ts-ignore
    ...forBrands(t, {
      gap: () => ({
        borderRadius: 25
      }),
      gapfs: () => ({
        borderRadius: 25
      }),
      on: () => ({
        borderRadius: 25
      }),
      at: () => ({
        borderRadius: 25
      }),
      br: () => ({
        borderRadius: 0
      }),
      brfs: () => ({
        borderRadius: 0
      }),
    }),
  });

  const getButtonHoverBackgroundColor = (t: Theme) => isBRRedesignEnabled ? t.color.wh : t.color.gray10;

  const getButtonHoverFontColor = (t: Theme) => isBRRedesignEnabled ? t.color.b2 : t.color.b1;

  const sizeButtonStyle = (theme: Theme): CSSObject => ({
    '&:hover': {
      backgroundColor: getButtonHoverBackgroundColor(theme),
      color: getButtonHoverFontColor(theme),
      ...getHoverStyle(theme)
    },
    ...theme.brandFont,
    appearance: 'none',
    minWidth: '2.5rem',
    overflow: 'visible',
    padding: '0.5rem',
    position: 'relative',
    verticalAlign: 'middle',
    border: 'none',
    boxSizing: 'border-box',
    height: '2.5rem',
    fontSize: applied ? '0.9375rem' : 'inherit',
    fontWeight: applied ? 'bold' : 'normal',
    textTransform: 'uppercase',
    // @ts-ignore
    ...forBrands(theme, {
      // @ts-ignore
      gap: getSizeButtonStyleForGap,
      // @ts-ignore
      gapfs: getSizeButtonStyleForGap,
      on: getSizeButtonStyleForOldNavy,
      at: getSizeButtonStyleForAthleta,
      br: getSizeButtonStyleForBananaRepublic,
      brfs: getSizeButtonStyleForBananaRepublic,
    }),
  });

  const sizeButtonDescriptionStyle = (): CSSObject => ({
    paddingTop: '0.1rem',
    paddingBottom: '0.1rem',
    display: 'block',
    fontWeight: 'normal',
    fontSize: isBRRedesignEnabled ? '0.75rem' : '0.8rem',
  });

  return (
    <button
      key={`${style}-${name}`}
      aria-checked={applied}
      aria-label={
        description
          ? `${variant} ${style} size ${name} ${description}`
          : `${variant} ${style} size ${name}`
      }
      css={sizeButtonStyle}
      onClick={onClick}
      role="checkbox"
      type="button"
    >
      {name}
      {description && (
        <span css={sizeButtonDescriptionStyle}>{description}</span>
      )}
    </button>
  );
};

export default SizeButton;

